import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import {
    makeStyles, Typography, IconButton,
} from '@material-ui/core';
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined';
import { useQuery, useMutation } from '@apollo/client';

import Table from 'components/widgets/Table';
import ConfirmDialog from 'components/widgets/modal/ConfirmDialog';
import UserSettingQuery from 'services/graphQL/query/setting/UserSettingQuery';
import UserSettingMutation from 'services/graphQL/mutate/setting/UserSettingMutation';
import KeyStore from 'utils/KeyStore';
import Permission from 'utils/enum/Permissions';
import { FetchPolicy } from 'utils/enum/Core';
import DateUtils, { DateFormat } from 'lib/DateUtils';
import ModalUtils from 'utils/ModalUtils';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flex: 1,
    },
    header: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    tableHeight: {
        display: 'contents',
    },
    deleteButton: {
        color: theme.palette.error.main,
    },
}));

const formatDate = (value) => {
    if (!value) return '-';
    const formatted = DateUtils.getFormattedDateInUserTimezone(value, DateFormat.SHORT_DATE_WITH_DASHES);
    return formatted || '-';
};

const TrustedDevicesSetting = ({ title }) => {
    const classes = useStyles();
    const keyStore = new KeyStore();
    const canWriteUser = keyStore.hasPermission(Permission.CORE_USER_WRITE);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deviceToDeactivate, setDeviceToDeactivate] = useState(null);

    const {
        loading,
        data,
        error,
        refetch,
    } = useQuery(
        UserSettingQuery.GET_ACTIVE_TRUSTED_DEVICES,
        { fetchPolicy: FetchPolicy.NETWORK_ONLY },
    );

    const [deactivateTrustedDevice, { loading: deactivating }] = useMutation(
        UserSettingMutation.DEACTIVATE_TRUSTED_DEVICE,
    );

    const openConfirmDeactivate = useCallback((e, trustedDeviceId) => {
        e.stopPropagation();
        setDeviceToDeactivate(trustedDeviceId);
        setConfirmOpen(true);
    }, []);

    const closeConfirm = useCallback(() => {
        setConfirmOpen(false);
        setDeviceToDeactivate(null);
    }, []);

    const handleConfirmDeactivate = useCallback(() => {
        if (!deviceToDeactivate) return;
        deactivateTrustedDevice({ variables: { trustedDeviceId: deviceToDeactivate } })
            .then(() => refetch())
            .then(() => {
                ModalUtils.successMessage(null, 'Item modified successfully');
                closeConfirm();
            })
            .catch((ex) => ModalUtils.errorMessage(ex?.graphQLErrors || [ex]));
    }, [deviceToDeactivate, deactivateTrustedDevice, refetch, closeConfirm]);

    if (error) {
        ModalUtils.errorMessage(error?.graphQLErrors || [error]);
    }

    const rows = data?.getActiveTrustedDevices ?? [];

    const columns = [
        {
            Header: 'UserAgent',
            accessor: 'userAgent',
        },
        {
            Header: 'Created On',
            accessor: 'createdOn',
            className: 'd-flex-justify-center-align-center',
            Cell: (record) => formatDate(record.value),
            maxWidth: 200,
        },
        {
            Header: 'Expires At',
            accessor: 'expiresAt',
            className: 'd-flex-justify-center-align-center',
            Cell: (record) => formatDate(record.value),
            maxWidth: 200,
        },
        ...(canWriteUser
            ? [{
                Header: 'Actions',
                accessor: 'trustedDeviceId',
                maxWidth: 100,
                className: 'd-flex-justify-center-align-center',
                Cell: (record) => (
                    <IconButton
                        size="small"
                        disabled={deactivating}
                        onClick={(e) => openConfirmDeactivate(e, record.original.trustedDeviceId)}
                    >
                        <DeleteOutlineOutlinedIcon className={classes.deleteButton} />
                    </IconButton>
                ),
            }]
            : []),
    ];

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <Typography variant="h4" style={{ fontSize: '1.125rem' }}>
                    {title}
                </Typography>
            </div>
            <div className={classes.tableHeight}>
                <Table
                    load={loading}
                    columns={columns}
                    data={rows}
                    totalRecords={rows.length}
                    unselectRow
                />
            </div>
            <ConfirmDialog
                title="Confirm deactivate device"
                description="Are you sure you want to deactivate this device?"
                open={confirmOpen}
                showHeader
                titlePrimary="Yes"
                titleSecondary="No"
                variant="outlined"
                dividerFooter={false}
                onClickPrimary={handleConfirmDeactivate}
                onClose={closeConfirm}
                onClickSecondary={closeConfirm}
            />
        </div>
    );
};

TrustedDevicesSetting.propTypes = {
    title: PropTypes.string,
};

TrustedDevicesSetting.defaultProps = {
    title: 'Trusted devices',
};

export default TrustedDevicesSetting;
